// Global error reporter for the LMV app.
//
// Installs window-level handlers that catch:
//   - uncaught exceptions (window 'error' event)
//   - unhandled promise rejections ('unhandledrejection' event)
//
// On error we:
//   1. Gather as much diagnostic info as possible (message, stack, route,
//      logged-in user, current factory / factory-setting / product JSON,
//      browser info, timestamp).
//   2. Copy that full payload as JSON to the clipboard.
//   3. Show an alert telling the user something went wrong and that the
//      details are on their clipboard.
//   4. Open a pre-filled mailto: email so the user can send it to support.
//
// The Svelte reactive system aborts a whole update cycle on the first
// thrown error, which is why "live calculation just stops working" -
// catching at window level lets us at least surface that to the user.
//
// NOTE: do NOT throw from any code in this file. If our error handler
// itself throws, the browser will swallow it and we'll have made the
// problem worse.

import { get } from 'svelte/store';
import { SourceMapConsumer } from 'source-map-js';
import { factoryStore } from '../store/factories.js';
import { loggedInUserStore } from '../store/loggedInUser.js';
import { constants } from './constants.js';

// Fallback support email - only used to build a mailto: link if the
// server-side report endpoint cannot be reached. The actual recipient
// of the auto-sent email is configured server-side via the
// "TechnicalSupportEmail" appsetting.
const SUPPORT_EMAIL = 'whishkid@gmail.com';
const APP_NAME = 'FairPrice (LMV)';
const REPORT_URL = (constants.baseUrl || '/') + 'api/Diagnostics/reporterror';

// ---------------------------------------------------------------
// Source-map based stack resolution
// ---------------------------------------------------------------
//
// Rollup emits `bundle.js.map` next to bundle.js (see rollup.config.js,
// `output.sourcemap: true`). We fetch and parse it once, cache the
// SourceMapConsumer, then rewrite every "url:line:col" reference inside
// the raw stack string back to the original .svelte / .js file.
//
// This is more reliable than stacktrace-js for our setup because:
//   - we only ever have one bundle, so one consumer covers everything;
//   - we operate on the raw stack text (regex replace) so eval frames
//     and weird stack shapes still get whatever can be mapped, with
//     the unmappable parts left alone;
//   - failures are localised to one frame, not the whole stack.

const _sourceMapConsumerCache = new Map(); // bundleUrl (no query) -> consumer (or null if failed)

async function getSourceMapConsumer(bundleUrl) {
    // Strip query string (e.g. "?version=...") so all variants share one
    // cache entry.
    const cleanBundleUrl = bundleUrl.split('?')[0];
    if (_sourceMapConsumerCache.has(cleanBundleUrl)) {
        return _sourceMapConsumerCache.get(cleanBundleUrl);
    }
    // Fire-and-cache a pending promise so concurrent errors don't fetch
    // the map multiple times.
    const promise = (async () => {
        try {
            // The bundle has a `//# sourceMappingURL=bundle.js.map`
            // trailer; we just assume "<bundle>.map" since that's what
            // rollup emits for this project.
            const mapUrl = cleanBundleUrl + '.map';
            const res = await fetch(mapUrl, { credentials: 'same-origin' });
            if (!res.ok) {
                // eslint-disable-next-line no-console
                console.warn(
                    '[LMV global error] source map fetch failed',
                    mapUrl,
                    res.status
                );
                return null;
            }
            const rawMap = await res.json();
            return new SourceMapConsumer(rawMap);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.warn(
                '[LMV global error] source map parse failed',
                bundleUrl,
                e
            );
            return null;
        }
    })();
    _sourceMapConsumerCache.set(cleanBundleUrl, promise);
    const consumer = await promise;
    // Replace pending promise with resolved value for fast hits later.
    _sourceMapConsumerCache.set(cleanBundleUrl, consumer);
    return consumer;
}

// Matches "https://host/path/bundle.js?query:LINE:COL" anywhere in a
// stack string. Captures: 1=full url, 2=line, 3=col.
const STACK_LOCATION_RE = /(https?:\/\/[^\s)]+?\.js(?:\?[^\s:)]*)?):(\d+):(\d+)/g;

async function resolveStackString(rawStack) {
    if (!rawStack) return '';

    // Collect all unique (url, line, col) tuples first.
    const matches = [];
    const seen = new Set();
    let m;
    STACK_LOCATION_RE.lastIndex = 0;
    while ((m = STACK_LOCATION_RE.exec(rawStack)) !== null) {
        const key = m[1] + '|' + m[2] + '|' + m[3];
        if (!seen.has(key)) {
            seen.add(key);
            matches.push({
                full: m[0],
                url: m[1],
                line: parseInt(m[2], 10),
                col: parseInt(m[3], 10),
            });
        }
    }
    if (matches.length === 0) return rawStack;

    // Group by bundle URL so we load each map only once.
    const byBundle = new Map();
    for (const item of matches) {
        if (!byBundle.has(item.url)) byBundle.set(item.url, []);
        byBundle.get(item.url).push(item);
    }

    // Resolve all bundles in parallel.
    const resolutions = new Map(); // "full" -> "file:line:col" (resolved) or null
    await Promise.all(
        Array.from(byBundle.entries()).map(async ([url, items]) => {
            const consumer = await getSourceMapConsumer(url);
            if (!consumer) {
                for (const it of items) resolutions.set(it.full, null);
                return;
            }
            for (const it of items) {
                try {
                    const orig = consumer.originalPositionFor({
                        line: it.line,
                        column: it.col,
                    });
                    if (orig && orig.source) {
                        const namePart = orig.name ? orig.name + ' @ ' : '';
                        resolutions.set(
                            it.full,
                            namePart +
                                orig.source +
                                ':' +
                                orig.line +
                                ':' +
                                orig.column
                        );
                    } else {
                        resolutions.set(it.full, null);
                    }
                } catch (e) {
                    resolutions.set(it.full, null);
                }
            }
        })
    );

    // Rebuild the stack string, replacing each known location with the
    // resolved one (keeping the original in parentheses for context).
    let out = rawStack;
    for (const [full, resolved] of resolutions.entries()) {
        if (!resolved) continue;
        // Escape regex special chars in `full`.
        const safe = full.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        out = out.replace(new RegExp(safe, 'g'), resolved + ' [' + full + ']');
    }
    return out;
}


// Debounce: avoid spamming the user when one bad render fires many
// errors in a row.
let lastAlertAt = 0;
const ALERT_COOLDOWN_MS = 4000;
let isHandling = false;

function safeGet(fn, fallback) {
    try {
        const v = fn();
        return v === undefined ? fallback : v;
    } catch (e) {
        return fallback;
    }
}

function safeStringify(obj) {
    try {
        const seen = new WeakSet();
        return JSON.stringify(
            obj,
            (key, value) => {
                if (typeof value === 'object' && value !== null) {
                    if (seen.has(value)) return '[Circular]';
                    seen.add(value);
                }
                if (value instanceof Error) {
                    return {
                        name: value.name,
                        message: value.message,
                        stack: value.stack,
                    };
                }
                return value;
            },
            2
        );
    } catch (e) {
        try {
            return String(obj);
        } catch (e2) {
            return '[unserializable]';
        }
    }
}

function collectDiagnostics(errorInfo) {
    const fs = safeGet(() => get(factoryStore), {});
    const user = safeGet(() => get(loggedInUserStore), {});

    const currentCalculator = safeGet(() => fs.currentCalculator, null);
    const currentFactory = safeGet(() => fs.currentFactory, null);

    // The factory setting (settings JSON) is normally referenced from the
    // product (currentCalculator.factorySettingId). We try to find it back
    // in the loaded factories collection.
    let currentFactorySetting = null;
    try {
        const factorySettingId =
            currentCalculator && currentCalculator.factorySettingId;
        if (factorySettingId && Array.isArray(fs.factories)) {
            for (const f of fs.factories) {
                if (!f || !Array.isArray(f.factorySettings)) continue;
                const match = f.factorySettings.find(
                    (s) => s && s.uuid === factorySettingId
                );
                if (match) {
                    currentFactorySetting = match;
                    break;
                }
            }
        }
    } catch (e) {
        // ignore
    }

    return {
        app: APP_NAME,
        timestamp: new Date().toISOString(),
        url: safeGet(() => window.location.href, ''),
        route: safeGet(() => window.location.hash, ''),
        userAgent: safeGet(() => navigator.userAgent, ''),
        language: safeGet(() => navigator.language, ''),
        platform: safeGet(() => navigator.platform, ''),
        online: safeGet(() => navigator.onLine, null),
        viewport: safeGet(
            () => ({ w: window.innerWidth, h: window.innerHeight }),
            null
        ),
        loggedInUser: {
            email: safeGet(() => user.email, ''),
            uuid: safeGet(() => user.uuid, ''),
        },
        error: errorInfo,
        currentProduct: currentCalculator,
        currentFactory: currentFactory,
        currentFactorySetting: currentFactorySetting,
        onlinemode: safeGet(() => fs.onlinemode, ''),
        lastSyncDate: safeGet(() => fs.lastSyncDate, 0),
    };
}

async function copyToClipboard(text) {
    // Try the modern async clipboard API first.
    try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch (e) {
        // fall through to legacy approach
    }
    // Legacy fallback - hidden textarea + execCommand.
    try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.top = '-1000px';
        ta.style.left = '-1000px';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        return !!ok;
    } catch (e) {
        return false;
    }
}

function buildMailto(diagnostics, clipboardWorked) {
    const subject = `[${APP_NAME}] Error report: ${
        (diagnostics.error && diagnostics.error.message) || 'unknown error'
    }`;

    // Keep the mailto body short - long URLs get truncated by mail
    // clients. The full payload lives on the clipboard.
    const lines = [
        'Hi FairPrice support team,',
        '',
        'I just hit an error in the app. Details below.',
        '',
        '----- Error -----',
        `Message: ${diagnostics.error && diagnostics.error.message}`,
        `When:    ${diagnostics.timestamp}`,
        `Where:   ${diagnostics.url}`,
        `User:    ${diagnostics.loggedInUser.email}`,
        '',
        '----- Browser -----',
        `UA:       ${diagnostics.userAgent}`,
        `Online:   ${diagnostics.online}`,
        `Viewport: ${
            diagnostics.viewport
                ? diagnostics.viewport.w + 'x' + diagnostics.viewport.h
                : ''
        }`,
        '',
        '----- Stack (truncated) -----',
        ((diagnostics.error && diagnostics.error.stack) || '').slice(0, 1500),
        '',
        '----- Full diagnostics -----',
        clipboardWorked
            ? 'The full diagnostics JSON (including the current factory settings and product JSON) has been copied to your clipboard. Please paste it below this line before sending:'
            : 'Clipboard copy failed - please attach the JSON shown in the alert / console.',
        '',
        '<<< paste here >>>',
        '',
        'Thanks!',
    ];

    const body = lines.join('\r\n');

    return (
        'mailto:' +
        encodeURIComponent(SUPPORT_EMAIL) +
        '?subject=' +
        encodeURIComponent(subject) +
        '&body=' +
        encodeURIComponent(body)
    );
}

async function postReportToServer(diagnostics) {
    try {
        const body = {
            message: (diagnostics.error && diagnostics.error.message) || '',
            stack: (diagnostics.error && diagnostics.error.stack) || '',
            resolvedStack: diagnostics.resolvedStack || '',
            url: diagnostics.url,
            userAgent: diagnostics.userAgent,
            userEmail:
                (diagnostics.loggedInUser && diagnostics.loggedInUser.email) ||
                '',
            timestamp: diagnostics.timestamp,
            payload: safeStringify(diagnostics),
        };
        // eslint-disable-next-line no-console
        console.log('[LMV global error] POST', REPORT_URL);
        const res = await fetch(REPORT_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        let respText = '';
        try {
            respText = await res.text();
        } catch (_) {
            /* ignore */
        }
        // eslint-disable-next-line no-console
        console.log(
            '[LMV global error] POST response',
            res.status,
            res.statusText,
            respText
        );
        return res.ok;
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[LMV global error] POST failed', e);
        return false;
    }
}

async function handle(errorInfo) {
    if (isHandling) return;
    const now = Date.now();
    if (now - lastAlertAt < ALERT_COOLDOWN_MS) return;
    lastAlertAt = now;
    isHandling = true;

    try {
        const diagnostics = collectDiagnostics(errorInfo);

        // Try to resolve the bundle stack back to original Svelte / JS
        // source positions using the .map file that rollup already
        // emits next to bundle.js.
        try {
            const rawStack = errorInfo && errorInfo.stack;
            if (rawStack) {
                const resolved = await resolveStackString(rawStack);
                if (resolved && resolved !== rawStack) {
                    diagnostics.resolvedStack = resolved;
                }
            }
        } catch (e) {
            // Source map fetch / parse failed - keep going with the raw
            // stack we already have.
            // eslint-disable-next-line no-console
            console.warn('[LMV global error] stack resolution failed', e);
        }

        const payload = safeStringify(diagnostics);

        // Always log to console - cheapest and most reliable.
        try {
            // eslint-disable-next-line no-console
            console.error(
                '[LMV global error]',
                errorInfo,
                diagnostics,
                diagnostics.resolvedStack
                    ? '\nResolved stack:\n' + diagnostics.resolvedStack
                    : ''
            );
        } catch (e) {
            /* ignore */
        }

        // Fire-and-await the server-side report. If it succeeds we don't
        // need to bother the user with a mailto: window.
        const sentToServer = await postReportToServer(diagnostics);
        const clipboardWorked = await copyToClipboard(payload);

        let userMsg;
        if (sentToServer) {
            userMsg =
                'Something went wrong in the app and a live calculation may have stopped updating.\n\n' +
                'Error: ' +
                ((errorInfo && errorInfo.message) || 'unknown error') +
                '\n\n' +
                'An automated error report has been sent to technical support. ' +
                (clipboardWorked
                    ? 'A full copy of the diagnostics has also been placed on your clipboard.\n\n'
                    : '\n\n') +
                'You may want to reload the page to recover.';
        } else {
            userMsg =
                'Something went wrong in the app and a live calculation may have stopped updating.\n\n' +
                'Error: ' +
                ((errorInfo && errorInfo.message) || 'unknown error') +
                '\n\n' +
                'We could not automatically send an error report. ' +
                (clipboardWorked
                    ? 'Full diagnostic details have been COPIED TO YOUR CLIPBOARD.\n\n'
                    : 'Diagnostic details could not be copied automatically (see browser console).\n\n') +
                'Click OK to open a pre-filled email to ' +
                SUPPORT_EMAIL +
                ' - please PASTE the details into the email body before sending, then reload the page.';
        }

        // Use setTimeout(0) so the alert is not raised from inside the
        // event handler stack (some browsers throttle that).
        window.setTimeout(() => {
            try {
                window.alert(userMsg);
            } catch (e) {
                /* ignore */
            }
            if (!sentToServer) {
                try {
                    const href = buildMailto(diagnostics, clipboardWorked);
                    // Open in a new tab/window so we don't navigate away
                    // from the (already broken) app state.
                    window.open(href, '_blank');
                } catch (e) {
                    /* ignore */
                }
            }
            isHandling = false;
        }, 0);
    } catch (e) {
        // Last-resort: never let the handler itself bubble.
        try {
            // eslint-disable-next-line no-console
            console.error('[LMV global error handler failed]', e);
        } catch (_) {
            /* ignore */
        }
        isHandling = false;
    }
}

function normalizeError(err) {
    let info;
    if (!err) {
        info = { message: 'unknown error', stack: '' };
    } else if (err instanceof Error) {
        info = { name: err.name, message: err.message, stack: err.stack || '' };
    } else if (typeof err === 'string') {
        info = { message: err, stack: '' };
    } else {
        try {
            info = {
                message:
                    err.message ||
                    err.reason ||
                    (typeof err === 'object' ? JSON.stringify(err) : String(err)),
                stack: err.stack || '',
            };
        } catch (e) {
            info = { message: String(err), stack: '' };
        }
    }
    // Stash the original Error object (if any) on a non-enumerable
    // property so handle() can hand it to StackTrace.fromError() for
    // sourcemap resolution without it ending up in the JSON payload.
    if (err instanceof Error) {
        try {
            Object.defineProperty(info, '_error', {
                value: err,
                enumerable: false,
            });
        } catch (e) {
            /* ignore */
        }
    }
    return info;
}

let installed = false;

export function installGlobalErrorHandler() {
    if (installed) return;
    installed = true;

    window.addEventListener('error', (ev) => {
        const info = normalizeError(ev.error || ev.message);
        info.source = ev.filename;
        info.lineno = ev.lineno;
        info.colno = ev.colno;
        handle(info);
    });

    window.addEventListener('unhandledrejection', (ev) => {
        const reason = ev.reason;
        const info = normalizeError(reason);
        info.kind = 'unhandledrejection';
        handle(info);
    });
}

// Exposed so a component can manually trigger the same flow
// (e.g. from a catch block in user code, or a "Report a problem" button).
export function reportError(err, extra) {
    const info = normalizeError(err);
    if (extra) info.extra = extra;
    handle(info);
}
